import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useData } from "../context/DataContext";
import { useLocation } from "react-router-dom";

const SEO = ({
  title,
  description,
  image,
  type = "website",
  article = false,
  author,
  publishedTime,
  tags = [],
}) => {
  const { profile } = useData();
  const { i18n } = useTranslation();
  const location = useLocation();
  const lang = i18n.language.split("-")[0];

  const siteName = profile?.settings?.siteName || "Saidqodirxon Portfolio";
  const siteUrl = "https://saidqodirxon.uz";
  const currentUrl = `${siteUrl}${location.pathname}`;

  const defaultTitle =
    profile?.settings?.metaTitle?.[lang] ||
    "Saidqodirxon Rahim | Full Stack JavaScript Developer";
  const defaultDesc =
    profile?.settings?.metaDescription?.[lang] ||
    "Professional Full Stack JavaScript Developer specializing in React, Node.js, Express, MongoDB. Expert in building modern web applications and REST APIs.";
  const defaultImage = `${siteUrl}/favicon.png`;

  const seoTitle = title ? `${title} | ${siteName}` : defaultTitle;
  const seoDescription = description || defaultDesc;
  const seoImage = image || defaultImage;
  const seoAuthor = author || "Saidqodirxon Rahim";

  // Structured Data (JSON-LD)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": article ? "Article" : "WebSite",
    name: seoTitle,
    description: seoDescription,
    url: currentUrl,
    image: seoImage,
    author: {
      "@type": "Person",
      name: seoAuthor,
      url: siteUrl,
    },
    ...(article &&
      publishedTime && {
        datePublished: publishedTime,
        publisher: {
          "@type": "Organization",
          name: siteName,
          logo: {
            "@type": "ImageObject",
            url: defaultImage,
          },
        },
      }),
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={lang} />
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="author" content={seoAuthor} />
      <link rel="canonical" href={currentUrl} />

      {/* Keywords */}
      {tags.length > 0 && <meta name="keywords" content={tags.join(", ")} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={article ? "article" : type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:locale"
        content={lang === "uz" ? "uz_UZ" : lang === "ru" ? "ru_RU" : "en_US"}
      />

      {article && publishedTime && (
        <>
          <meta property="article:published_time" content={publishedTime} />
          <meta property="article:author" content={seoAuthor} />
          {tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:creator" content="@saidqodirxon" />

      {/* Additional SEO */}
      <meta
        name="robots"
        content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
      />
      <meta name="googlebot" content="index, follow" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
